// امضا و مهر خود ارائه‌دهنده در حساب من SCR-STAFF-002 (US-IAM-024، AP-IAM-05): فقط خود ارائه‌دهنده بارگذاری یا حذف می‌کند (AC-IAM-024.1)؛
// PNG با پس‌زمینه شفاف تا ۳۰۰ KB؛ پیش‌نمایش از Endpoint خود (ثبت مشاهده). روی سندی درج می‌شود که خود او امضای دیجیتال کرده است.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

/** کد خطای «کاربر ارائه‌دهنده نیست». */
export const NOT_A_PROVIDER = 'PHX-IAM-0407';

const signatureKeys = {
  own: (tenantId: string | undefined) => ['identity', tenantId, 'me', 'provider-signature'] as const,
  image: (tenantId: string | undefined, kind: string, version: number) =>
    ['identity', tenantId, 'me', 'provider-signature', kind, version] as const,
};

type Kind = 'signature' | 'stamp';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read'));
    reader.readAsDataURL(file);
  });
}

function Preview({ kind, version }: { kind: Kind; version: number }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const image = useQuery({
    queryKey: signatureKeys.image(session?.tenantId, kind, version),
    queryFn: async () => {
      const result = await tenantApi.GET('/api/v1/identity/me/provider-signature/{kind}', {
        params: { path: { kind } },
        parseAs: 'blob',
      });
      if (!result.response.ok || !result.data) {
        throw result.error ?? new Error('image');
      }
      return URL.createObjectURL(result.data as Blob);
    },
    staleTime: Infinity,
  });
  useEffect(() => () => {
    if (image.data) {
      URL.revokeObjectURL(image.data);
    }
  }, [image.data]);
  if (!image.data) {
    return null;
  }
  return (
    <Box
      component="img"
      src={image.data}
      alt={t(`iam:signature.${kind}`)}
      data-testid={`signature-preview-${kind}`}
      sx={{ maxHeight: 80, maxWidth: 220, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default', p: 0.5 }}
    />
  );
}

export function ProviderSignatureSection() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>();
  const permitted = access.can('iam.provider.signature.manage_own');

  const own = useQuery({
    queryKey: signatureKeys.own(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/me/provider-signature')),
    enabled: permitted,
    retry: false,
  });

  const upload = useMutation({
    mutationFn: async ({ kind, file }: { kind: Kind; file: File }) => {
      const dataUrl = await readAsDataUrl(file);
      return unwrap(
        tenantApi.PUT('/api/v1/identity/me/provider-signature', {
          body: kind === 'signature' ? { signature: dataUrl, stamp: null } : { signature: null, stamp: dataUrl },
        }),
      );
    },
    onMutate: () => setError(undefined),
    onSuccess: (view) => queryClient.setQueryData(signatureKeys.own(session?.tenantId), view),
    onError: setError,
  });
  const clear = useMutation({
    mutationFn: () => unwrap(tenantApi.DELETE('/api/v1/identity/me/provider-signature')),
    onSuccess: (view) => queryClient.setQueryData(signatureKeys.own(session?.tenantId), view),
    onError: setError,
  });

  if (!permitted) {
    return null;
  }

  const notProvider = isApiError(own.error) && own.error.code === NOT_A_PROVIDER;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="provider-signature">
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('iam:signature.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.5 }}>
        {t('iam:signature.hint')}
      </Typography>
      {own.isPending ? <LoadingState rows={2} /> : null}
      {notProvider ? <Alert severity="info">{t('iam:signature.not_provider')}</Alert> : null}
      {own.error && !notProvider ? <ErrorState error={own.error} onRetry={() => void own.refetch()} /> : null}
      {own.data ? (
        <Stack spacing={2}>
          {(['signature', 'stamp'] as const).map((kind) => {
            const present = kind === 'signature' ? own.data.hasSignature : own.data.hasStamp;
            return (
              <Stack key={kind} direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
                <Typography sx={{ minWidth: 80 }}>{t(`iam:signature.${kind}`)}</Typography>
                {present ? <Preview kind={kind} version={Number(own.data.version)} /> : <Typography color="text.secondary">{t('iam:signature.none')}</Typography>}
                <Button component="label" variant="outlined" size="small" disabled={upload.isPending}>
                  {t(present ? 'iam:signature.replace' : 'iam:signature.upload')}
                  <input
                    hidden
                    type="file"
                    accept="image/png"
                    data-testid={`signature-file-${kind}`}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        upload.mutate({ kind, file });
                      }
                      event.target.value = '';
                    }}
                  />
                </Button>
              </Stack>
            );
          })}
          {error ? <ErrorState error={error} compact /> : null}
          {own.data.hasSignature || own.data.hasStamp ? (
            <Box>
              <Button color="error" size="small" onClick={() => clear.mutate()} disabled={clear.isPending} data-testid="signature-clear">
                {t('iam:signature.clear')}
              </Button>
            </Box>
          ) : null}
        </Stack>
      ) : null}
    </Paper>
  );
}
